import { jsx } from '@emotion/react';
import React, { useState, useRef, useEffect, Children } from 'react';
import ChevronLeft from '../icons/ChevronLeftIcon';
import ChevronRight from '../icons/ChevronRightIcon';
import { useAdaptiveRender } from '@argo/utils';
import { NavContainer, NavButton, ActiveIndicator, Indicator, TabsContainer, StyledTabs, StyledContent } from './styles';
import { TabContent } from './styles';
import { handleActiveIndicator, renderActiveTabContent, renderChildrenWithTabs, handleAriaMove, handleKeyPress } from './util';
const hideOrDisplayButtons = (position, tabs, setHideLeftNav, setHideRightNav) => {
  const boundary = 4;
  if (tabs && position >= 0) {
    const distanceFromEnd = tabs.scrollWidth - (position + tabs.clientWidth);
    if (position <= boundary) {
      setHideLeftNav(true);
      setHideRightNav(false);
    } else if (distanceFromEnd <= boundary) {
      setHideRightNav(true);
      setHideLeftNav(false);
    } else {
      setHideLeftNav(false);
      setHideRightNav(false);
    }
  }
};
const scrollToCenter = (setScrollPosition, targetTab, tabs) => {
  if (tabs && targetTab) {
    const position = targetTab.offsetLeft;
    const width = targetTab.offsetWidth;
    const scrollToPosition = Math.max(0, position - tabs.clientWidth / 2 + width / 2);
    tabs.scrollLeft = scrollToPosition;
    setScrollPosition(scrollToPosition);
  }
};
const navigationScroll = (tabs, direction, scrollPosition, setScrollPosition) => {
  if (tabs) {
    const defaultDistance = 200;
    const distanceFromEnd = tabs.scrollWidth - (scrollPosition + tabs.clientWidth);
    const rightScroll = distanceFromEnd < defaultDistance ? distanceFromEnd : defaultDistance;
    const scrollToPosition = direction === 'right' ? scrollPosition + rightScroll : Math.max(0, scrollPosition - defaultDistance);
    tabs.scrollLeft = scrollToPosition;
    setScrollPosition(scrollToPosition);
  }
};
export const renderSeoFriendlyTabs = (props, activeTabIndex) => {
  const children = props.children;
  return Children.map(children, (child, i) => jsx(TabContent, {
    active: activeTabIndex === i
  }, child.props.children));
};
const NavTabs = props => {
  const tabsRef = useRef(null);
  const tabsScrollWidth = tabsRef && tabsRef.current && tabsRef.current.scrollWidth;
  const indicatorRef = useRef(null);
  const {
    isNarrow,
    isNarrowBreakpoint,
    isDivider,
    isInverse,
    navColor,
    isSeoFriendly,
    dynamicTabIndex = 0,
    dataTestId = '',
    ifsticky,
    showActiveIndicator = true,
    contentMinHeight,
    contentPaddingTop,
    enableTabsAccessibility,
    onTabSelected: _onTabSelected,
    ...rest
  } = props;
  const [activeTabIndex, setTabIndex] = useState(props.activeTabIndex || 0);
  const [showNavButtons, setShowNavButtons] = useState(false);
  const [hideLeftNav, setHideLeftNav] = useState(false);
  const [hideRightNav, setHideRightNav] = useState(false);
  const {
    width
  } = useAdaptiveRender();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeAriaFocusIndex, setAriaFocusIndex] = useState(props.activeTabIndex || props.dynamicTabIndex || 0);
  const [keyPressed, setKeyPress] = useState(false);
  const targetTab = tab => {
    if (tabsRef.current) {
      scrollToCenter(setScrollPosition, tab, tabsRef.current);
    }
  };
  const handleScroll = () => {
    if (tabsRef.current) {
      hideOrDisplayButtons(tabsRef.current.scrollLeft, tabsRef.current, setHideLeftNav, setHideRightNav);
    }
  };
  useEffect(() => {
    const tabs = tabsRef.current;
    if (tabs) {
      tabs.addEventListener('scroll', handleScroll);
      return () => {
        tabs.removeEventListener('scroll', handleScroll);
      };
    }
    return;
  }, []);
  useEffect(() => {
    if (tabsRef.current) {
      if (tabsRef.current.scrollWidth > tabsRef.current.clientWidth) {
        setShowNavButtons(true);
      } else {
        setShowNavButtons(false);
      }
      const activeChild = tabsRef.current.children[activeTabIndex];
      targetTab(activeChild);
      handleActiveIndicator(tabsRef, indicatorRef, activeTabIndex, true);
    }
  }, [activeTabIndex, width, ifsticky, dynamicTabIndex, tabsScrollWidth]);
  useEffect(() => {
    if (tabsRef.current) {
      hideOrDisplayButtons(scrollPosition, tabsRef.current, setHideLeftNav, setHideRightNav);
    }
  }, [scrollPosition, showNavButtons]);
  useEffect(() => {
    setTabIndex(dynamicTabIndex);
  }, [dynamicTabIndex]);
  useEffect(() => {
    handleAriaMove(tabsRef, activeAriaFocusIndex, keyPressed);
  }, [activeAriaFocusIndex]);
  return jsx(React.Fragment, null, jsx("div", null, jsx(TabsContainer, rest, jsx(NavContainer, {
    showNav: showNavButtons
  }, jsx(NavButton, {
    className: "nav-button-left",
    direction: "left",
    navColor: navColor,
    "data-lean-auto": "tabs-nav-button-left",
    "data-testid": dataTestId,
    hide: hideLeftNav,
    "aria-label": "see the content on the left",
    isInverse: isInverse,
    onClick: () => {
      tabsRef.current && navigationScroll(tabsRef.current, 'left', scrollPosition, setScrollPosition);
    }
  }, jsx(ChevronLeft, {
    size: "24"
  })), jsx(NavButton, {
    className: "nav-button-right",
    direction: "right",
    navColor: navColor,
    isInverse: isInverse,
    "data-lean-auto": "tabs-nav-button-right",
    "aria-label": "see the content on the right",
    hide: hideRightNav,
    "data-testid": dataTestId,
    onClick: () => {
      tabsRef.current && navigationScroll(tabsRef.current, 'right', scrollPosition, setScrollPosition);
    }
  }, jsx(ChevronRight, {
    size: "24"
  }))), jsx("div", null, jsx(StyledTabs, {
    className: "styled-nav-tabs",
    isNarrow: isNarrow,
    isNarrowBreakpoint: isNarrowBreakpoint,
    isInverse: isInverse,
    ref: tabsRef,
    role: 'tablist',
    onKeyDown: enableTabsAccessibility ? event => handleKeyPress(event, props, activeAriaFocusIndex, activeTabIndex, setAriaFocusIndex, setKeyPress) : undefined
  }, renderChildrenWithTabs(props, activeTabIndex, setTabIndex, setAriaFocusIndex, targetTab), showActiveIndicator && jsx(ActiveIndicator, {
    isInverse: isInverse,
    ref: indicatorRef
  }))), !isDivider && jsx(Indicator, {
    isInverse: isInverse
  }))), jsx(StyledContent, {
    className: "navtabs-styled-content",
    minHeight: contentMinHeight,
    contentPaddingTop: contentPaddingTop
  }, isSeoFriendly ? renderSeoFriendlyTabs(props, activeTabIndex) : renderActiveTabContent(props, activeTabIndex)));
};
NavTabs.displayName = 'Tabs';
NavTabs.defaultProps = {
  isNarrow: false,
  isNarrowBreakpoint: 'max',
  isInverse: false,
  isDivider: false,
  activeTabIndex: 0,
  enableNavigation: false,
  showActiveIndicator: true
};
export default NavTabs;
//# sourceMappingURL=NavTabs.js.map